import React from 'react';
import { Folder as FolderIcon } from 'lucide-react';
import { CellContext, ColumnDef, Row, filterFns, flexRender, type FilterFn } from '@tanstack/react-table';
import { Folder, FolderTree } from '@/hooks/useFolders';
import { Link, type useNavigate } from '@tanstack/react-router';
import { FolderActionsDropdown } from './FolderActionsDropdown';
import { AnalysisSetBadge } from '@/components/deep-analysis/AnalysisSetBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type NavigateFn = ReturnType<typeof useNavigate>;

/** Direct subfolder count + recursive document count for a folder, used for the folder-icon hover tooltip. */
export type FolderContentCounts = Map<number, { folderCount: number; fileCount: number }>;

/** Hover label for an inline folder icon, e.g. "Folder containing 2 files and 1 folder". */
function formatFolderContentsTooltip(folderCount: number, fileCount: number): string {
  if (folderCount === 0 && fileCount === 0) return 'Empty folder';
  const parts: string[] = [];
  if (fileCount > 0) {
    parts.push(`${fileCount} file${fileCount === 1 ? '' : 's'}`);
  }
  if (folderCount > 0) {
    parts.push(`${folderCount} folder${folderCount === 1 ? '' : 's'}`);
  }
  return `Folder containing ${parts.join(' and ')}`;
}

/**
 * The folder whose documents must be loaded for the current table view. Direct child
 * folders are shown as inline rows (clicking one navigates into it); their documents are
 * not fetched here. Folder-row badges come from the folder tree via
 * {@link buildFolderContentCounts}.
 */
export function getFolderIdsToFetch(
  rootFolderId: number | undefined,
): number[] | undefined {
  if (rootFolderId == null) return undefined;
  return [rootFolderId];
}

/**
 * Precompute folder-row badge counts from the folder tree so we don't have to fetch documents
 * from every descendant folder just to display a count. `folderCount` is the number of direct
 * subfolders; `fileCount` is the tree's recursive document count (the folder and everything
 * nested beneath it).
 *
 * Counts are only recorded for folders present in the tree. Folders missing from the tree
 * (e.g. while it's still loading, or folders excluded from the analysis set in Deep Analysis
 * mode) are intentionally omitted so {@link buildFolderTableData} falls back to counting the
 * documents/subfolders it has actually fetched rather than showing a stale zero count.
 *
 * Deriving `folderCount` from the tree (rather than the flat folder list) also keeps Deep
 * Analysis counts consistent with what's rendered: the tree is loaded filtered to the analysis
 * set, so excluded subfolders aren't counted. In non-DA mode the tree is unfiltered, so counts
 * match the full folder list.
 */
export function buildFolderContentCounts(
  tree: FolderTree[] | undefined,
): FolderContentCounts {
  const counts: FolderContentCounts = new Map();
  const walk = (nodes: FolderTree[] | undefined) => {
    (nodes || []).forEach((node) => {
      counts.set(node.folder_id, {
        folderCount: (node.children ?? []).length,
        fileCount: node.document_count ?? 0,
      });
      walk(node.children);
    });
  };
  walk(tree);
  return counts;
}

/**
 * Collect every folder_id present in a folder tree. Used to restrict inline folder rows to the
 * analysis set when the tree is loaded with `analysisSetOnly` (the flat folder list is unfiltered).
 */
export function buildFolderTreeIdSet(tree: FolderTree[] | undefined): Set<number> {
  const ids = new Set<number>();
  const walk = (nodes: FolderTree[] | undefined) => {
    (nodes || []).forEach((node) => {
      ids.add(node.folder_id);
      walk(node.children);
    });
  };
  walk(tree);
  return ids;
}

/** Flatten a folder tree into a guid -> FolderTree lookup (FolderActionsDropdown needs the richer tree node). */
export function buildFolderTreeMap(tree: FolderTree[] | undefined): Map<string, FolderTree> {
  const map = new Map<string, FolderTree>();
  const walk = (nodes: FolderTree[] | undefined) => {
    (nodes || []).forEach((node) => {
      map.set(node.guid, node);
      walk(node.children);
    });
  };
  walk(tree);
  return map;
}

const navigateToFolder = (
  navigate: NavigateFn,
  slug: string,
  folderGuid: string,
  deepAnalysisOnly?: boolean,
) => {
  navigate({
    to: '/matter/$slug/case-files/folder/$folderGuid',
    params: { slug, folderGuid },
    search: deepAnalysisOnly ? { view: 'deep-analysis' } : {},
  });
};

export const FOLDER_FIRST_SORT_COLUMN_ID = '__folderFirst';

type FolderRowMeta = {
  id: string;
  isFolderRow?: boolean;
  folderGuid?: string;
  folderId?: number;
  childFolderCount?: number;
  childFileCount?: number;
};

type SyntheticFolderRow<TDoc extends { id: string }> = FolderRowMeta & {
  isFolderRow: true;
  guid: string;
  fileName: string;
  displayName: string;
  summary: string;
  tags: unknown[];
  date: string;
  folderGuid: string;
  folderId: number;
  childFolderCount: number;
  childFileCount: number;
};

export type FolderTableRow<TDoc extends { id: string }> = (TDoc & FolderRowMeta) | SyntheticFolderRow<TDoc>;

export function getFolderTableHandlers<TDoc extends { id: string }>(opts: {
  slug: string;
  navigate: NavigateFn;
  openDocumentPanel: (opts: { documentGuid: string; activeTab: 'document' }) => void;
  canExpandDocumentRow: (row: Row<TDoc>) => boolean;
  deepAnalysisOnly?: boolean;
}) {
  const onAnyRowClick = (
    row: FolderTableRow<TDoc>,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    if (row.isFolderRow && row.folderGuid) {
      if (event.metaKey || event.ctrlKey) {
        const href = `/matter/${opts.slug}/case-files/folder/${row.folderGuid}${opts.deepAnalysisOnly ? '?view=deep-analysis' : ''}`;
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      navigateToFolder(opts.navigate, opts.slug, row.folderGuid, opts.deepAnalysisOnly);
    }
  };

  const getRowClassName = (row: FolderTableRow<TDoc>) => {
    if (row.isFolderRow) return 'cursor-pointer';
    return '';
  };

  const getRowCanExpand = (row: Row<FolderTableRow<TDoc>>) => {
    const typed = row.original as FolderTableRow<TDoc>;
    if (typed.isFolderRow) return false;
    return opts.canExpandDocumentRow(row as Row<TDoc>);
  };

  const onRowActivate = (row: FolderTableRow<TDoc>) => {
    if (row.isFolderRow) return;
    opts.openDocumentPanel({ documentGuid: row.id, activeTab: 'document' });
  };

  const onRowDoubleClick = (row: FolderTableRow<TDoc>) => {
    if (row.isFolderRow && row.folderGuid) {
      navigateToFolder(opts.navigate, opts.slug, row.folderGuid, opts.deepAnalysisOnly);
      return;
    }
    opts.openDocumentPanel({ documentGuid: row.id, activeTab: 'document' });
  };

  return {
    onAnyRowClick,
    getRowClassName,
    getRowCanExpand,
    onRowActivate,
    onRowDoubleClick,
  };
}

export function buildFolderTableData<TDoc extends { id: string }>(
  folders: Folder[],
  currentFolderId: number | undefined,
  documents: TDoc[],
  folderContentCounts?: FolderContentCounts,
  allowedFolderIds?: Set<number>,
): FolderTableRow<TDoc>[] {
  const getDocumentFolderId = (doc: TDoc): number | undefined => {
    const folderIdValue = (doc as any)?.document_type?.id;
    if (folderIdValue === undefined || folderIdValue === null) return undefined;
    const parsed = Number(folderIdValue);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  // Without a root folder there's no hierarchy to build (e.g. the correspondence page renders
  // before its folder resolves, or that folder is absent). In that case useDocuments fetches the
  // whole matter unfiltered, so return every fetched document flat rather than silently dropping
  // the ones that carry a folder id.
  if (currentFolderId == null) {
    return [...(documents || [])];
  }

  const folderByParent = new Map<number, Folder[]>();
  folders.forEach((folder) => {
    if (typeof folder.parent_folder_id !== 'number') return;
    if (!folderByParent.has(folder.parent_folder_id)) {
      folderByParent.set(folder.parent_folder_id, []);
    }
    folderByParent.get(folder.parent_folder_id)?.push(folder);
  });

  const documentsByFolderId = new Map<number, TDoc[]>();
  const unassignedDocuments: TDoc[] = [];
  (documents || []).forEach((doc) => {
    const folderId = getDocumentFolderId(doc);
    if (folderId === undefined) {
      unassignedDocuments.push(doc);
      return;
    }
    if (!documentsByFolderId.has(folderId)) {
      documentsByFolderId.set(folderId, []);
    }
    documentsByFolderId.get(folderId)?.push(doc);
  });

  const children = (folderByParent.get(currentFolderId) || [])
    // When an allow-list is supplied (Deep Analysis mode), only show folders in the analysis set.
    .filter((folder) => !allowedFolderIds || allowedFolderIds.has(folder.folder_id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const folderRows: FolderTableRow<TDoc>[] = children.map((childFolder) => {
    // Prefer precomputed tree counts (so child folders don't need their documents fetched);
    // fall back to counting fetched documents when no counts map is supplied.
    const precomputedCounts = folderContentCounts?.get(childFolder.folder_id);
    const directChildFolderCount = precomputedCounts?.folderCount
      ?? (folderByParent.get(childFolder.folder_id) || []).length;
    const directChildFileCount = precomputedCounts?.fileCount
      ?? (documentsByFolderId.get(childFolder.folder_id) || []).length;
    const folderRow: SyntheticFolderRow<TDoc> = {
      id: `folder-${childFolder.guid}`,
      guid: childFolder.guid,
      fileName: childFolder.name,
      displayName: childFolder.name,
      summary: '',
      tags: [],
      // Keep folder rows at the top under default date-desc sorting.
      date: '9999-12-31T00:00:00.000Z',
      isFolderRow: true,
      folderGuid: childFolder.guid,
      folderId: childFolder.folder_id,
      childFolderCount: directChildFolderCount,
      childFileCount: directChildFileCount,
    };
    return folderRow;
  });

  const currentFolderDocuments = documentsByFolderId.get(currentFolderId) || [];
  return [...folderRows, ...currentFolderDocuments, ...unassignedDocuments];
}

type FolderCellContext<TDoc extends { id: string }> = CellContext<FolderTableRow<TDoc>, unknown>;

/** Narrow a folder-table cell context to its synthetic folder row. Only call inside an `isFolderRow` guard. */
function asFolderRow<TDoc extends { id: string }>(ctx: FolderCellContext<TDoc>): SyntheticFolderRow<TDoc> {
  return ctx.row.original as SyntheticFolderRow<TDoc>;
}

/** Columns whose filters also apply to inline folder rows (match on the folder name). */
const FOLDER_APPLICABLE_FILTER_COLUMN_IDS = new Set(['fileName']);

/**
 * Wrap a base column's filter for mixed file + folder tables.
 *
 * Folder rows only participate in name filtering so a Name search can match folders.
 * Other column filters (date, summary, tags, semantic-search ids, etc.) hide folders,
 * since those fields don't apply to them. Non-folder rows delegate to the column's
 * original filter, resolving string built-ins through `filterFns`.
 */
function makeFolderRowFilter<TDoc extends { id: string }>(
  baseFilterFn: ColumnDef<TDoc>['filterFn'],
): FilterFn<FolderTableRow<TDoc>> {
  return (row, columnId, filterValue, addMeta) => {
    if (row.original.isFolderRow && !FOLDER_APPLICABLE_FILTER_COLUMN_IDS.has(columnId)) {
      return false;
    }
    const resolved =
      typeof baseFilterFn === 'function'
        ? baseFilterFn
        : typeof baseFilterFn === 'string'
          ? filterFns[baseFilterFn as keyof typeof filterFns]
          // Filterable columns all specify an explicit filterFn; this fallback only guards the
          // (unfiltered) undefined case so non-folder rows keep sensible default matching.
          : filterFns.includesString;
    return (resolved as FilterFn<FolderTableRow<TDoc>>)(row, columnId, filterValue, addMeta);
  };
}

export function enhanceColumnsForFolderRows<TDoc extends { id: string }>(
  baseColumns: ColumnDef<TDoc>[],
  opts: {
    matterSlug: string;
    onRefresh: () => void;
    foldersByGuid?: Map<string, FolderTree>;
    emptyColumnIds?: string[];
    showFolderNameInSummary?: boolean;
    deepAnalysisEnabled?: boolean;
    deepAnalysisOnly?: boolean;
  },
): ColumnDef<FolderTableRow<TDoc>>[] {
  const emptyColumnIds = opts.emptyColumnIds || ['summary', 'tags', 'from', 'to', 'attachments', 'date'];
  const folderFirstSortColumn: ColumnDef<FolderTableRow<TDoc>> = {
    id: FOLDER_FIRST_SORT_COLUMN_ID,
    accessorFn: (row) => (row.isFolderRow ? 1 : 0),
    header: () => null,
    cell: () => null,
    enableSorting: true,
    enableColumnFilter: false,
    size: 0,
    minSize: 0,
    maxSize: 0,
  };

  const enhancedColumns = [folderFirstSortColumn, ...(baseColumns as ColumnDef<FolderTableRow<TDoc>>[]).map((column) => {
    const columnId = column.id ?? ('accessorKey' in column ? String(column.accessorKey ?? '') : '');
    const renderOriginalCell = (ctx: FolderCellContext<TDoc>) =>
      column.cell ? flexRender(column.cell, ctx) : null;

    if (columnId === 'actions') {
      return {
        ...column,
        cell: (ctx: FolderCellContext<TDoc>) => {
          if (ctx.row.original.isFolderRow) {
            const folderRow = asFolderRow(ctx);
            const folder = folderRow.folderGuid ? opts.foldersByGuid?.get(folderRow.folderGuid) : undefined;
            if (!folder) return null;
            return (
              <div
                className="flex flex-row items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <FolderActionsDropdown
                  matterSlug={opts.matterSlug}
                  folder={folder}
                  onRefresh={opts.onRefresh}
                  deepAnalysisEnabled={opts.deepAnalysisEnabled}
                />
              </div>
            );
          }
          return renderOriginalCell(ctx);
        },
      };
    }

    if (columnId === 'fileType') {
      // The fileType accessor runs computeFileType on the row's fileName. Synthetic folder rows
      // carry the folder name (extensionless), which computeFileType classifies as 'Other',
      // inflating the File type facet counts. Return undefined for folder rows so the faceted
      // filter skips them (it ignores non-iterable keys).
      const baseAccessorFn = 'accessorFn' in column ? column.accessorFn : undefined;
      return {
        ...column,
        accessorFn: (row: FolderTableRow<TDoc>, index: number) =>
          row.isFolderRow ? undefined : baseAccessorFn?.(row, index),
        cell: (ctx: FolderCellContext<TDoc>) => {
          if (ctx.row.original.isFolderRow) {
            const folderRow = asFolderRow(ctx);
            const folder = folderRow.folderGuid ? opts.foldersByGuid?.get(folderRow.folderGuid) : undefined;
            const showAnalysisSetBadge = Boolean(opts.deepAnalysisEnabled && folder?.analysis_set_effective);
            const analysisSetTooltip = folder?.analysis_set_state === 'included'
              ? 'This folder is included in Deep Analysis. Files in it will be processed unless explicitly excluded.'
              : 'This folder is included in Deep Analysis through a parent folder.';
            const contentsTooltip = formatFolderContentsTooltip(
              Number(folderRow.childFolderCount || 0),
              Number(folderRow.childFileCount || 0),
            );
            return (
              <div className="relative flex items-center justify-start">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <FolderIcon viewBox="1 2 22 19" preserveAspectRatio="xMinYMid meet" className="h-5 w-5 text-amber-500/40 fill-amber-400/15" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{contentsTooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {showAnalysisSetBadge ? (
                  <span
                    className="absolute -right-1 -top-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AnalysisSetBadge active compact iconOnly tooltip={analysisSetTooltip} />
                  </span>
                ) : null}
              </div>
            );
          }
          return renderOriginalCell(ctx);
        },
      };
    }

    if (columnId === 'fileName') {
      return {
        ...column,
        cell: (ctx: FolderCellContext<TDoc>) => {
          if (ctx.row.original.isFolderRow) {
            const folderRow = asFolderRow(ctx);
            return (
              <div className="font-medium">
                <Link
                  to="/matter/$slug/case-files/folder/$folderGuid"
                  params={{ slug: opts.matterSlug, folderGuid: folderRow.folderGuid }}
                  search={opts.deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
                  className="text-left cursor-pointer text-gray-700 hover:text-primary-500 transition-colors duration-150"
                  onClick={(e) => {
                    // Keep row-level handlers from firing; keep native modifier-click behavior.
                    e.stopPropagation();
                  }}
                >
                  {folderRow.displayName || folderRow.fileName}
                </Link>
              </div>
            );
          }
          return renderOriginalCell(ctx);
        },
      };
    }

    if (columnId === 'summary') {
      return {
        ...column,
        cell: (ctx: FolderCellContext<TDoc>) => {
          if (ctx.row.original.isFolderRow) {
            if (!opts.showFolderNameInSummary) return null;
            const folderRow = asFolderRow(ctx);
            const folderName = folderRow.displayName || folderRow.fileName;
            return (
              <Link
                to="/matter/$slug/case-files/folder/$folderGuid"
                params={{ slug: opts.matterSlug, folderGuid: folderRow.folderGuid }}
                search={opts.deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
                className="text-left cursor-pointer hover:text-primary-500 transition-colors duration-150"
                title={folderName}
                onClick={(e) => {
                  // Keep row-level handlers from firing; keep native modifier-click behavior.
                  e.stopPropagation();
                }}
              >
                {folderName}
              </Link>
            );
          }
          return renderOriginalCell(ctx);
        },
      };
    }

    if (emptyColumnIds.includes(columnId)) {
      return {
        ...column,
        cell: (ctx: FolderCellContext<TDoc>) => {
          if (ctx.row.original.isFolderRow) {
            return null;
          }
          return renderOriginalCell(ctx);
        },
      };
    }

    return column;
  })];

  // Name filters match folders; other column filters hide them.
  return enhancedColumns.map((column) =>
    column.id === FOLDER_FIRST_SORT_COLUMN_ID
      ? column
      : { ...column, filterFn: makeFolderRowFilter((column as ColumnDef<TDoc>).filterFn) },
  );
}
